/**
 * Physics — static triangle BVH, character controller, rigid bodies, ragdolls.
 *
 * Implements the frozen `ctx.physics` API from CONTRACTS.md exactly:
 *
 *   addStatic(object3D, opts?)   removeStatic(object3D)   rebuild()
 *   raycast(origin, dir, far?, mask?)      -> { point, normal, distance, object, surface, body } | null
 *   raycastAll(origin, dir, far?, mask?)   -> hits[] sorted near->far
 *   sphereCast(origin, dir, radius, far?, mask?)
 *   capsuleMove(pos, halfHeight, radius, delta, opts?)
 *        -> { position, grounded, groundNormal, hitWall, steppedUp }
 *   overlapCapsule(pos, halfHeight, radius)  pointInSolid(p)
 *   addBody(opts)  removeBody(body)  applyRadialImpulse(centre, radius, power)
 *   MASK = { WORLD:1, PROP:2, CHARACTER:4, DEBRIS:8, ALL:0xffff }
 *
 * Extensions (additive — nothing above was renamed or removed):
 *
 *   physics.ready            Promise, resolves when the initial BVH build finishes
 *   physics.gravity          9.81 * 2.2 (magnitude, m/s²) — as the old stub exposed
 *   physics.makeRagdoll(spec)          see Ragdoll.js for the full spec shape
 *   physics.removeRagdoll(r)
 *   physics.makeRagdollVisual(r, mat?) placeholder capsule mesh group for a ragdoll
 *   physics.debrisMesh                 THREE.InstancedMesh (unit boxes) driven by
 *                                      every body created WITHOUT its own `mesh`.
 *                                      512 slots, one draw call. FX may read
 *                                      `.instanceMatrix` / `.count` freely, and may
 *                                      swap `.material`. Do NOT write matrices into
 *                                      it — physics owns the transforms.
 *   physics.debrisSphereMesh           same, for shape:'sphere' bodies
 *   physics.setDebug(flags)            { bvh, bodies, contacts, capsules } | bool
 *   physics.surfaceAt(point)           surface enum under a world point
 *   physics.stats()                    build + per-frame timings
 *
 * owner: physics
 */
import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { makeRng } from '../core/Rand.js';
import { TriBVH } from './BVH.js';
import { Queries, makeMoveResult } from './Queries.js';
import { BodyWorld } from './Bodies.js';
import { Ragdoll } from './Ragdoll.js';
import { PhysicsDebug } from './Debug.js';
import { SIM_DT } from '../core/Engine.js';

export const MASK = { WORLD: 1, PROP: 2, CHARACTER: 4, DEBRIS: 8, ALL: 0xffff };
const GRAVITY = 9.81 * 2.2;
// Physics test rig origin. It has to sit INSIDE the volume the render pipeline
// actually draws — parked out at z = -72 it came back solid black whatever we
// did with materials or lights — but it must not touch the map. The level's
// collision tops out at y = 16.3, so we float it above the rooftops: nothing to
// intersect, still fully lit and fogged like the rest of the scene.
const RIG_X = 0, RIG_Y = 26, RIG_Z = -8;

export function createPhysicsSystem() {
  const bvh = new TriBVH();
  const queries = new Queries(bvh);
  const world = new BodyWorld(queries, { gravity: -GRAVITY, staticMask: MASK.WORLD | MASK.PROP });
  const ragdolls = [];
  const statics = [];
  let debug = null;
  let ctxRef = null;
  let groundPlane = null;
  let dirty = false;
  let shotCamActive = false;
  const shotCamPos = new THREE.Vector3();
  let resolveReady;
  const ready = new Promise((r) => { resolveReady = r; });
  const frameStats = { bvhMs: 0, bodyMs: 0, queryMs: 0, ragdollMs: 0, rays: 0, sweeps: 0 };

  /* ---------------------------------------------------------------- *
   * static registration
   * ---------------------------------------------------------------- */
  function addStatic(object3D, opts = {}) {
    if (!object3D) return;
    if (statics.some((s) => s.object === object3D)) return;
    statics.push({ object: object3D, mask: opts.mask ?? MASK.WORLD });
    dirty = true;
  }
  function removeStatic(object3D) {
    const i = statics.findIndex((s) => s.object === object3D);
    if (i >= 0) { statics.splice(i, 1); dirty = true; }
  }
  function rebuild() {
    const t0 = performance.now();
    bvh.setSources(statics.map((s) => s.object), MASK.WORLD);
    // per-source mask override
    if (statics.some((s) => s.mask !== MASK.WORLD)) applySourceMasks();
    bvh.build();
    dirty = false;
    frameStats.bvhMs = performance.now() - t0;
    if (debug) { debug.invalidateBVH(); debug.update(bvh, world.bodies, null); }
    return bvh.stats;
  }
  async function rebuildAsync() {
    const t0 = performance.now();
    bvh.setSources(statics.map((s) => s.object), MASK.WORLD);
    if (statics.some((s) => s.mask !== MASK.WORLD)) applySourceMasks();
    await bvh.buildAsync(8);
    dirty = false;
    frameStats.bvhMs = performance.now() - t0;
    if (debug) { debug.invalidateBVH(); debug.update(bvh, world.bodies, null); }
    return bvh.stats;
  }
  function applySourceMasks() {
    // triangles inherit their source object's mask unless the mesh overrides it
    const byObject = new Map();
    for (const s of statics) s.object.traverse((o) => byObject.set(o, s.mask));
    for (let i = 0; i < bvh.triCount; i++) {
      const mesh = bvh.objects[bvh.obj[i]];
      if (!mesh) continue;
      if (mesh.userData.physicsMask !== undefined) continue;
      const m = byObject.get(mesh);
      if (m !== undefined) bvh.mask[i] = m;
    }
  }

  /* ---------------------------------------------------------------- *
   * dynamic-body ray tests (so weapons can shoot dropped props)
   * ---------------------------------------------------------------- */
  function rayBodies(origin, dir, far, mask) {
    if ((mask & (MASK.PROP | MASK.DEBRIS | MASK.CHARACTER)) === 0) return null;
    let bestT = far, best = null;
    for (const b of world.bodies) {
      if ((b.mask & mask) === 0) continue;
      let t = -1;
      if (b.shape === 'sphere') t = raySphere(origin, dir, b.position, b.radius);
      else t = rayOBB(origin, dir, b);
      if (t >= 0 && t < bestT) { bestT = t; best = b; }
    }
    if (!best) return null;
    _p.copy(dir).multiplyScalar(bestT).add(origin);
    _n.copy(_p).sub(best.position);
    if (best.shape === 'box') {
      // snap the normal to the dominant face
      _n.applyQuaternion(_q.copy(best.quaternion).conjugate());
      const ax = Math.abs(_n.x) / best.half.x, ay = Math.abs(_n.y) / best.half.y, az = Math.abs(_n.z) / best.half.z;
      if (ax >= ay && ax >= az) _n.set(Math.sign(_n.x), 0, 0);
      else if (ay >= az) _n.set(0, Math.sign(_n.y), 0);
      else _n.set(0, 0, Math.sign(_n.z));
      _n.applyQuaternion(best.quaternion);
    } else _n.normalize();
    return {
      point: _p.clone(), normal: _n.clone(), distance: bestT,
      object: best.mesh || world.debrisMesh, surface: best.userData.surface || 'metal',
      triangle: -1, body: best,
    };
  }

  function raySphere(o, d, c, r) {
    const ox = o.x - c.x, oy = o.y - c.y, oz = o.z - c.z;
    const b = ox * d.x + oy * d.y + oz * d.z;
    const cc = ox * ox + oy * oy + oz * oz - r * r;
    const disc = b * b - cc;
    if (disc < 0) return -1;
    const s = Math.sqrt(disc);
    const t0 = -b - s, t1 = -b + s;
    return t0 >= 0 ? t0 : (t1 >= 0 ? t1 : -1);
  }

  function rayOBB(o, d, body) {
    const e = body._matrix.elements;
    const dx = o.x - body.position.x, dy = o.y - body.position.y, dz = o.z - body.position.z;
    let tmin = -Infinity, tmax = Infinity;
    const h = [body.half.x, body.half.y, body.half.z];
    for (let i = 0; i < 3; i++) {
      const ax = e[i * 4], ay = e[i * 4 + 1], az = e[i * 4 + 2];
      const p = dx * ax + dy * ay + dz * az;
      const f = d.x * ax + d.y * ay + d.z * az;
      if (Math.abs(f) < 1e-9) { if (-p - h[i] > 0 || -p + h[i] < 0) return -1; continue; }
      let t0 = (-p - h[i]) / f, t1 = (-p + h[i]) / f;
      if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
      if (t0 > tmin) tmin = t0;
      if (t1 < tmax) tmax = t1;
      if (tmin > tmax) return -1;
    }
    return tmin >= 0 ? tmin : (tmax >= 0 ? tmax : -1);
  }

  /* ---------------------------------------------------------------- *
   * the frozen API surface
   * ---------------------------------------------------------------- */
  const api = {
    MASK,
    gravity: GRAVITY,
    ready,
    bvh, queries, world,
    get debrisMesh() { return world.debrisMesh; },
    get debrisSphereMesh() { return world.debrisSphereMesh; },
    get ragdolls() { return ragdolls; },

    addStatic, removeStatic, rebuild, rebuildAsync,

    raycast(origin, dir, far = 500, mask = MASK.ALL) {
      const t0 = performance.now();
      const s = queries.raycast(origin, dir, far, mask);
      const d = _d.copy(dir).normalize();
      const b = rayBodies(origin, d, s ? s.distance : far, mask);
      frameStats.queryMs += performance.now() - t0;
      if (b && (!s || b.distance < s.distance)) return b;
      return s;
    },

    raycastAll(origin, dir, far = 500, mask = MASK.ALL) {
      const hits = queries.raycastAll(origin, dir, far, mask);
      const d = _d.copy(dir).normalize();
      const b = rayBodies(origin, d, far, mask);
      if (b) { hits.push(b); hits.sort((x, y) => x.distance - y.distance); }
      return hits;
    },

    sphereCast(origin, dir, radius, far = 100, mask = MASK.ALL) {
      return queries.sphereCast(origin, dir, radius, far, mask);
    },

    capsuleMove(pos, halfHeight, radius, delta, opts) {
      const t0 = performance.now();
      const r = queries.capsuleMove(pos, halfHeight, radius, delta, opts);
      frameStats.queryMs += performance.now() - t0;
      return r;
    },

    overlapCapsule(pos, halfHeight, radius, mask = MASK.ALL) {
      return queries.overlapCapsule(pos, halfHeight, radius, mask);
    },

    pointInSolid(p, mask = MASK.WORLD) { return queries.pointInSolid(p, mask); },

    /** Push a capsule out of anything it is inside. Mutates `pos`. */
    depenetrateCapsule(pos, halfHeight, radius, mask = MASK.ALL) {
      return queries.depenetrate(pos, halfHeight, radius, mask);
    },

    addBody(opts) { return world.add(opts); },
    removeBody(body) { return world.remove(body); },
    clearBodies() { world.clear(); },
    applyRadialImpulse(centre, radius, power) { return world.applyRadialImpulse(centre, radius, power); },

    makeRagdoll(spec) {
      const r = new Ragdoll(queries, spec, -GRAVITY);
      r._onDispose = (x) => {
        const i = ragdolls.indexOf(x);
        if (i >= 0) ragdolls.splice(i, 1);
        if (x._visualRoot) {
          x._visualRoot.removeFromParent();
          x._visualRoot.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
          x._visualRoot = null;
          x.visual = null;
        }
      };
      ragdolls.push(r);
      return r;
    },
    removeRagdoll(r) { if (r) r.dispose(); },
    makeRagdollVisual(ragdoll, material) {
      return makeRagdollVisual(ragdoll, material || ctxRef?.materials?.get('skin'));
    },

    setDebug(flags) { return debug ? debug.setEnabled(flags) : false; },
    get debug() { return debug; },

    surfaceAt(point, mask = MASK.WORLD) {
      _d.set(0, -1, 0);
      _o.set(point.x, point.y + 2, point.z);
      const h = queries.raycast(_o, _d, 6, mask);
      return h ? h.surface : 'concrete';
    },

    stats() {
      return {
        tris: bvh.triCount,
        nodes: bvh.nodeCount,
        buildMs: bvh.stats.ms,
        buildSlices: bvh.stats.slices,
        maxDepth: bvh.stats.maxDepth,
        bodies: world.stats.bodies,
        awake: world.stats.awake,
        contacts: world.stats.contacts,
        pairs: world.stats.pairs,
        bodyMs: frameStats.bodyMs,
        queryMs: frameStats.queryMs,
        ragdollMs: frameStats.ragdollMs,
        ragdolls: ragdolls.length,
        groundFallback: !!groundPlane,
      };
    },
  };

  /* ---------------------------------------------------------------- *
   * the System
   * ---------------------------------------------------------------- */
  let rig = null;

  return {
    name: 'physics',
    priority: 25,

    async init(ctx) {
      ctxRef = ctx;
      ctx.physics = api;

      if (ctx.config.debug) {
        debug = new PhysicsDebug(ctx.scene);
        queries.recordDebug = false;
      }

      // instanced debris meshes (one draw call for the whole pile)
      const boxMat = ctx.materials?.get('wood_crate') || new THREE.MeshStandardMaterial({ color: 0x8a7a5c, roughness: 0.85 });
      const sphMat = ctx.materials?.get('metal_rusted') || new THREE.MeshStandardMaterial({ color: 0x6a6a70, roughness: 0.6, metalness: 0.4 });
      world.createInstancedMeshes(ctx.scene, boxMat, sphMat);

      // register level collision
      registerLevel(ctx);
      await rebuildAsync();
      if (ensureGround(ctx)) await rebuildAsync();
      resolveReady(api);

      // explosions push debris around
      ctx.events.on('fx:explosion', (e) => {
        if (!e || !e.position) return;
        world.applyRadialImpulse(e.position, e.radius ?? 6, e.power ?? 120);
      });

      registerShots(ctx);
    },

    fixed(dt, ctx) {
      if (dirty) rebuild();
      if (bvh.triCount === 0 && ctx.level) { registerLevel(ctx); if (dirty) rebuild(); }

      let t0 = performance.now();
      world.step(dt);
      frameStats.bodyMs = performance.now() - t0;

      t0 = performance.now();
      for (let i = ragdolls.length - 1; i >= 0; i--) {
        const r = ragdolls[i];
        if (r.disposed) { ragdolls.splice(i, 1); continue; }
        r.step(dt);
      }
      frameStats.ragdollMs = performance.now() - t0;
      frameStats.queryMs = 0;
    },

    update(dt, ctx) {
      world.syncAll();
      for (const r of ragdolls) if (r.visual) syncRagdollVisual(r);
      if (debug && debug.group.visible) {
        debug.update(bvh, world.bodies, queries.debugContacts);
      }
      // While one of our own debug shots owns the camera, hide the first-person
      // viewmodel — it covers a quarter of the frame and this is a diagnostic
      // view, not a gameplay one. The moment anything else moves the camera the
      // condition fails and the viewmodel comes straight back, so this can never
      // leak into another team's shot.
      if (shotCamActive) {
        const mine = ctx.camera.position.distanceToSquared(shotCamPos) < 1e-4;
        ctx.viewScene.visible = !mine;
        if (!mine) shotCamActive = false;
      }
    },

    dispose() {
      world.clear();
      if (debug) debug.dispose();
      if (rig) rig.removeFromParent();
    },
  };

  /* ---------------------------------------------------------------- *
   * level wiring
   * ---------------------------------------------------------------- */
  function registerLevel(ctx) {
    const level = ctx.level;
    if (!level) return;
    const list = (Array.isArray(level.collision) && level.collision.length)
      ? level.collision
      : (level.root ? [level.root] : []);
    for (const o of list) addStatic(o, { mask: MASK.WORLD });
  }

  /**
   * Ground safety net.
   *
   * `ctx.level.collision` currently contains only *blockers* — the level's
   * ground is built with `groundPatch(..., collide: false)`, so a ray straight
   * down anywhere in the play area hits nothing and everything (player, debris,
   * ragdolls) falls out of the world. Until the level team collides its ground,
   * we synthesise a physics-only floor across `navBounds`, tessellated on a 2 m
   * grid with each cell's surface taken from `level.surfaceAt()` so ballistics
   * and footsteps still report the right material.
   *
   * The probe is re-run on every rebuild: the moment the level ships real ground
   * collision, this stops adding anything.
   */
  function ensureGround(ctx) {
    const level = ctx.level;
    if (!level || bvh.triCount === 0) return false;
    if (groundPlane) return false;

    const box = level.navBounds || new THREE.Box3(new THREE.Vector3(-40, 0, -40), new THREE.Vector3(40, 8, 40));
    const samples = [];
    const sp = [...(level.spawnPoints || []), ...(level.enemySpawns || [])];
    for (const p of sp) samples.push([p.x, p.z]);
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        samples.push([
          THREE.MathUtils.lerp(box.min.x + 2, box.max.x - 2, i / 4),
          THREE.MathUtils.lerp(box.min.z + 2, box.max.z - 2, j / 4),
        ]);
      }
    }
    // A single downward raycast is not a ground test: inside a building it hits
    // the roof, and on a prop it hits the crate. Use raycastAll and only count a
    // sample if SOMETHING solid exists near ground level under it.
    let hits = 0;
    _d.set(0, -1, 0);
    const far = box.max.y - box.min.y + 12;
    const groundTop = box.min.y + 2.5;
    for (const [x, z] of samples) {
      _o.set(x, box.max.y + 4, z);
      const all = queries.raycastAll(_o, _d, far, MASK.WORLD);
      for (const h of all) {
        if (h.point.y <= groundTop && h.normal.y > 0.5) { hits++; break; }
      }
    }
    if (hits > samples.length * 0.6) return false;   // the level does collide its ground

    console.info(`[physics] level ground has no collision (${hits}/${samples.length} down-rays hit) — adding a synthetic floor across navBounds`);

    const step = 2;
    const x0 = Math.floor(box.min.x / step) * step - step;
    const x1 = Math.ceil(box.max.x / step) * step + step;
    const z0 = Math.floor(box.min.z / step) * step - step;
    const z1 = Math.ceil(box.max.z / step) * step + step;
    const buckets = new Map();
    const surfaceAt = typeof level.surfaceAt === 'function' ? level.surfaceAt : () => 'concrete';
    for (let x = x0; x < x1; x += step) {
      for (let z = z0; z < z1; z += step) {
        let s = 'concrete';
        try { s = surfaceAt(_o.set(x + step * 0.5, 0.05, z + step * 0.5)) || 'concrete'; } catch (e) { /* keep default */ }
        let arr = buckets.get(s);
        if (!arr) { arr = []; buckets.set(s, arr); }
        // two triangles, wound CCW seen from above so the normal is +Y
        arr.push(
          x, 0, z, x, 0, z + step, x + step, 0, z + step,
          x, 0, z, x + step, 0, z + step, x + step, 0, z,
        );
      }
    }

    groundPlane = new THREE.Group();
    groundPlane.name = 'physics-fallback-ground';
    groundPlane.visible = false;
    for (const [surface, verts] of buckets) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
      const m = new THREE.Mesh(g, _invisibleMat);
      m.name = `physics-ground:${surface}`;
      m.userData.surface = surface;
      m.userData.physicsMask = MASK.WORLD;
      m.visible = false;
      groundPlane.add(m);
    }
    groundPlane.updateMatrixWorld(true);
    addStatic(groundPlane, { mask: MASK.WORLD });
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Shots
   * ---------------------------------------------------------------- */
  function registerShots(ctx) {
    /**
     * Park the camera for a shot.
     *
     * `look()` alone is not enough: the player system re-writes ctx.camera from
     * its rig in every `update()`, so a shot camera set with `look()` is gone by
     * the next frame. We use the player module's documented shot hooks
     * (`place` + `freeze`) and then pin the rig's output transform.
     */
    function shotCam(c, from, at, fov) {
      look(c, from, at, fov);
      const dx = at[0] - from[0], dy = at[1] - from[1], dz = at[2] - from[2];
      const yaw = Math.atan2(-dx, -dz);
      const pitch = Math.atan2(dy, Math.hypot(dx, dz));
      shotCamPos.set(from[0], from[1], from[2]);
      shotCamActive = true;                 // hides the viewmodel, see update()

      const P = c.get?.('player');
      if (!P) return;
      // `place` may throw while the player module is mid-refactor; the freeze
      // and the rig pin must happen regardless, and `place` un-freezes, so the
      // order and the separate try blocks both matter.
      try {
        if (typeof P.place === 'function') {
          const eyeH = P.rig?.eyeHeight ?? 1.65;
          P.place(new THREE.Vector3(from[0], from[1] - eyeH, from[2]), yaw, pitch);
        }
      } catch (e) { /* fall through — freeze + pin below is what actually matters */ }
      try { if (typeof P.freeze === 'function') P.freeze(true); } catch (e) { /* noop */ }
      try {
        if (P.rig) {
          P.rig.eye.set(from[0], from[1], from[2]);
          P.rig.fov = fov;
          P.rig.quaternion.setFromEuler(_euler.set(pitch, yaw, 0, 'YXZ'));
          P.rig.applyTo(c);
        }
      } catch (e) { /* the look() above is the fallback */ }
    }

    /**
     * Pick a camera position that can actually SEE the target. The level is
     * built by another team and moves under us — a hard-coded viewpoint ends up
     * inside a crate or behind a jersey barrier. Orbit the target and use our
     * own raycast to find the first clear line of sight.
     */
    function clearShotCam(c, target, dist, height, fov) {
      const tx = target[0], ty = target[1], tz = target[2];
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2 + 0.6;
        const fx = tx + Math.cos(a) * dist, fy = ty + height, fz = tz + Math.sin(a) * dist;
        _o.set(fx, fy, fz);
        if (queries.overlapCapsule(_o, 0.25, 0.30, MASK.WORLD)) continue;   // camera inside geometry
        _d.set(tx - fx, ty - fy, tz - fz);
        const len = _d.length();
        _d.multiplyScalar(1 / len);
        if (queries.raycast(_o, _d, len - 0.25, MASK.WORLD)) continue;      // something in the way
        shotCam(c, [fx, fy, fz], target, fov);
        return [fx, fy, fz];
      }
      const fallback = [tx + dist, ty + height, tz + dist];
      shotCam(c, fallback, target, fov);
      return fallback;
    }

    /* ---- a deterministic stair + ramp + kerb rig, physics-owned ---- */
    function buildRig() {
      if (rig) return rig;
      const g = new THREE.Group();
      g.name = 'physics-test-rig';
      // Parked well outside the level's collision bounds (z >= -44) so it can
      // never intersect the map, and it carries its own floor slab so the shots
      // are valid whatever the level does with ground collision.
      g.position.set(RIG_X, RIG_Y, RIG_Z);
      // Use the level's own materials: now that the rig floats inside the volume
      // the renderer actually draws, the lighting team's sun and the shared post
      // stack treat it exactly like level geometry, which is the only way it
      // stays legible as they iterate on exposure and fog.
      const wood = ctx.materials.get('wood_plank');
      const conc = ctx.materials.get('concrete_wall');
      const metal = ctx.materials.get('metal_painted');
      const stepMat = [ctx.materials.get('concrete_floor'), ctx.materials.get('concrete_wall')];

      const add = (mat, sx, sy, sz, x, y, z, surface, rx = 0) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), mat);
        m.position.set(x, y, z);
        if (rx) m.rotation.x = rx;
        m.castShadow = true; m.receiveShadow = true;
        m.userData.surface = surface;
        g.add(m);
        return m;
      };

      // The rig carries its own floor so the shots stay valid whatever the
      // level does about ground collision. Every lane runs from +z to -z.
      add(conc, 14, 0.5, 15, 0, -0.25, -0.5, 'concrete');

      // Lane A (x = 0): 8-step staircase, 0.18 m rise / 0.32 m tread.
      for (let i = 0; i < 8; i++) {
        const h = (i + 1) * 0.18;
        add(stepMat[i & 1], 3, h, 0.32, 0, h * 0.5, 2.0 - i * 0.32, 'concrete');
      }
      add(conc, 3, 1.44, 2.4, 0, 0.72, -1.28, 'concrete');          // top landing
      add(conc, 3, 2.60, 0.4, 0, 1.30, -2.68, 'concrete');          // back wall

      // Lane B (x = +4.5): 20° ramp rising toward -z, with a landing.
      add(wood, 3, 0.22, 5.0, 4.5, 0.782, 0, 'wood', 20 * Math.PI / 180);
      add(wood, 3, 1.74, 2.0, 4.5, 0.87, -3.31, 'wood');
      add(wood, 3, 2.60, 0.4, 4.5, 1.30, -4.51, 'wood');            // back wall

      // Lane C (x = -4.5): a 0.42 m kerb (must be stepped over) followed by a
      // 0.62 m block (must NOT be — it is above the 0.45 m threshold).
      add(metal, 3, 0.42, 1.4, -4.5, 0.21, 1.6, 'metal');
      add(metal, 3, 0.62, 1.4, -4.5, 0.31, -1.2, 'metal');

      rig = g;
      return g;
    }

    function setRig(on) {
      const g = buildRig();
      if (on && !g.parent) { ctx.scene.add(g); addStatic(g, { mask: MASK.WORLD }); rebuild(); }
      else if (!on && g.parent) { g.removeFromParent(); removeStatic(g); rebuild(); }
    }

    /** Walk a capsule with the real controller and record its path. */
    function walk(localX, localZ, dirZ, steps = 460, speed = 3.0) {
      const radius = 0.32, halfHeight = 0.58;
      const pos = new THREE.Vector3(
        RIG_X + localX, RIG_Y + radius + halfHeight + 0.02, RIG_Z + localZ,
      );
      queries.depenetrate(pos, halfHeight, radius, MASK.ALL);
      let vy = 0, grounded = false, maxStep = 0, totalStep = 0;
      const path = [];
      const move = new THREE.Vector3();
      for (let i = 0; i < steps; i++) {
        vy = grounded ? -1.2 : vy - GRAVITY * SIM_DT;
        move.set(0, vy * SIM_DT, dirZ * speed * SIM_DT);
        const r = queries.capsuleMove(pos, halfHeight, radius, move, {
          mask: MASK.ALL, stepHeight: 0.45, grounded,
        });
        pos.copy(r.position);
        grounded = r.grounded;
        if (grounded && vy < 0) vy = 0;
        if (r.steppedUp > maxStep) maxStep = r.steppedUp;
        totalStep += r.steppedUp;
        if (i % 14 === 0) path.push(pos.x, pos.y, pos.z);
      }
      return { pos, path, maxStep, totalStep, radius, halfHeight };
    }

    registerShot('phys_bvh', (c) => {
      setRig(false);
      world.clear();
      for (const r of ragdolls.slice()) r.dispose();
      if (debug) {
        debug.clearMarkers();
        debug.bvhMaxDepth = 10;
        debug.bvhLeavesOnly = false;
        debug.setEnabled({ bvh: true, bodies: false, contacts: false, capsules: false });
        debug.update(bvh, world.bodies, null);
      }
      shotCam(c, [27, 21, 27], [-1, 2.0, 0], 50);
    }, { desc: 'static triangle BVH wireframe over the level', settle: 4 });

    registerShot('phys_slope', (c) => {
      setRig(true);
      world.clear();
      for (const r of ragdolls.slice()) r.dispose();
      if (debug) {
        debug.clearMarkers();
        debug.setEnabled({ bvh: false, bodies: false, contacts: true, capsules: true });
      }
      queries.debugContacts.length = 0;
      queries.recordDebug = true;

      const stairs = walk(0, 5.0, -1, 400, 3.0);              // up the staircase
      const ramp = walk(4.5, 5.0, -1, 400, 3.0);              // up the 20° ramp
      const kerb = walk(-4.5, 5.0, -1, 300, 3.0);             // 0.42 kerb, then blocked
      queries.recordDebug = false;
      // decimate the recorded contacts — thousands of crosses is just noise
      {
        const src = queries.debugContacts, keep = [];
        for (let i = 0, k = 0; i + 5 < src.length; i += 6, k++) {
          if (k % 9 === 0) for (let j = 0; j < 6; j++) keep.push(src[i + j]);
        }
        queries.debugContacts = keep;
      }

      if (debug) {
        for (const w of [stairs, ramp, kerb]) {
          for (let i = 0; i + 2 < w.path.length; i += 3) {
            debug.markCapsule(w.path[i], w.path[i + 1], w.path[i + 2], w.halfHeight, w.radius);
          }
          for (let i = 0; i + 5 < w.path.length; i += 3) {
            debug.markLine(w.path[i], w.path[i + 1], w.path[i + 2], w.path[i + 3], w.path[i + 4], w.path[i + 5]);
          }
        }
        debug.update(bvh, world.bodies, queries.debugContacts);
      }
      api._lastWalk = { stairs: stairs.pos.toArray(), stairsStep: stairs.maxStep, ramp: ramp.pos.toArray(), kerb: kerb.pos.toArray() };
      shotCam(c, [RIG_X + 8.6, RIG_Y + 7.4, RIG_Z + 8.6], [RIG_X - 0.2, RIG_Y + 0.7, RIG_Z - 0.7], 50);
    }, { desc: 'capsule walked up stairs / ramp / kerb with step-up debug', settle: 4 });

    registerShot('phys_debris', (c) => {
      setRig(false);
      for (const r of ragdolls.slice()) r.dispose();
      world.clear();
      if (debug) { debug.clearMarkers(); debug.setEnabled({ bvh: false, bodies: true, contacts: false, capsules: false }); }

      // The market square's centre is occupied by the fountain, so the pile is
      // dropped on the open asphalt of the south spawn courtyard.
      const DX = -1.0, DZ = -27.0;
      const rng = makeRng(0x5eed11);
      for (let i = 0; i < 120; i++) {
        const a = rng() * Math.PI * 2;
        const rad = Math.sqrt(rng()) * 1.55;
        const sphere = rng() < 0.22;
        const s = 0.12 + rng() * 0.20;
        world.add({
          shape: sphere ? 'sphere' : 'box',
          size: sphere ? s * 0.8 : { x: s, y: s * (0.35 + rng() * 0.9), z: s * (0.6 + rng() * 0.8) },
          radius: s * 0.4,
          mass: 2 + rng() * 4,
          restitution: 0.12 + rng() * 0.14,
          friction: 0.68,
          position: new THREE.Vector3(DX + Math.cos(a) * rad, 1.3 + i * 0.05, DZ + Math.sin(a) * rad),
          quaternion: new THREE.Quaternion().setFromEuler(
            new THREE.Euler(rng() * 6.28, rng() * 6.28, rng() * 6.28),
          ),
          velocity: new THREE.Vector3((rng() - 0.5) * 0.7, -1.2, (rng() - 0.5) * 0.7),
          mask: MASK.DEBRIS,
        });
      }
      // settle deterministically inside the shot so the screenshot shows a pile at rest
      for (let i = 0; i < 1800; i++) world.step(SIM_DT);
      world.syncAll();
      api._lastDebris = {
        asleep: world.bodies.filter((b) => b.sleeping).length,
        total: world.bodies.length,
        lowestAabb: Math.min(...world.bodies.map((b) => b.aabbMin.y)),
        maxSpeed: Math.max(...world.bodies.map((b) => b.velocity.length())),
        centre: [DX, DZ],
      };
      // The AABB overlay is what makes this a physics shot rather than a render
      // one: 120 outlines, every one flush with the ground, none interpenetrating.
      if (debug) debug.update(bvh, world.bodies, null);
      api._lastDebris.cam = clearShotCam(c, [DX, 0.25, DZ], 3.0, 1.5, 36);
    }, { desc: '120 rigid bodies settled into a pile on the level', settle: 4 });

    registerShot('phys_ragdoll', (c) => {
      setRig(true);
      world.clear();
      for (const r of ragdolls.slice()) r.dispose();
      if (debug) { debug.clearMarkers(); debug.setEnabled(false); }

      const dolls = [];
      for (let k = 0; k < 2; k++) {
        const doll = api.makeRagdoll({
          position: [RIG_X + (k === 0 ? -0.55 : 0.45), RIG_Y + 2.7, RIG_Z + 1.35 + k * 0.9],
          quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(k ? 0.45 : -0.35, k ? 1.1 : 0.35, 0.2)),
          velocity: [k ? -0.35 : 0.3, -1.0, k ? -0.9 : -1.4],
          seed: 4242 + k * 17,
          angularKick: 0.7,
          mask: MASK.WORLD,
        });
        const vis = api.makeRagdollVisual(doll, ragdollMat(ctx));
        ctx.scene.add(vis);
        doll._visualRoot = vis;
        dolls.push(doll);
      }
      for (let i = 0; i < 2600; i++) for (const d of dolls) d.step(SIM_DT);
      for (const d of dolls) syncRagdollVisual(d);
      api._lastRagdoll = {
        sleeping: dolls.map((d) => d.sleeping),
        pelvis: dolls.map((d) => d.bone('pelvis').position.toArray().map((v) => +v.toFixed(3))),
        stretch: dolls.map((d) => {
          let m = 0;
          for (let i = 0; i < d.boneCount; i++) {
            const a = d.boneA[i] * 3, b = d.boneB[i] * 3;
            const len = Math.hypot(d.pos[b] - d.pos[a], d.pos[b + 1] - d.pos[a + 1], d.pos[b + 2] - d.pos[a + 2]);
            m = Math.max(m, Math.abs(len - d.boneRest[i]) / d.boneRest[i]);
          }
          return +m.toFixed(4);
        }),
      };
      shotCam(c, [RIG_X + 3.6, RIG_Y + 3.3, RIG_Z + 5.2], [RIG_X - 0.3, RIG_Y + 0.7, RIG_Z + 0.9], 46);
    }, { desc: 'two ragdolls dropped onto stairs and settled', settle: 4 });
  }
}

/** A distinctly tinted clone so placeholder ragdolls read against concrete. */
let _ragMat = null;
function ragdollMat(ctx) {
  if (_ragMat) return _ragMat;
  try {
    _ragMat = ctx.materials.make ? ctx.materials.make('cloth_camo') : ctx.materials.get('cloth_camo').clone();
  } catch (e) {
    _ragMat = new THREE.MeshStandardMaterial({ roughness: 0.9 });
  }
  if (_ragMat.color) _ragMat.color.setRGB(0.42, 0.20, 0.10);   // reads against concrete
  return _ragMat;
}

/* ------------------------------------------------------------------ *
 * Ragdoll placeholder visual — capsule meshes driven by the bone transforms.
 * The AI team can use this until their skinned meshes land.
 * ------------------------------------------------------------------ */
function makeRagdollVisual(ragdoll, material) {
  const g = new THREE.Group();
  g.name = 'ragdoll-visual';
  g.userData.noCollide = true;
  const mat = material || new THREE.MeshStandardMaterial({ color: 0x5a6152, roughness: 0.9 });
  ragdoll.visual = [];
  for (const b of ragdoll.bones) {
    const len = Math.max(0.02, b.length);
    const geo = new THREE.CapsuleGeometry(b.radius * 0.72, len, 4, 10);
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true; m.receiveShadow = true;
    m.userData.noCollide = true;
    g.add(m);
    ragdoll.visual.push(m);
  }
  syncRagdollVisual(ragdoll);
  return g;
}

function syncRagdollVisual(ragdoll) {
  if (!ragdoll.visual) return;
  for (let i = 0; i < ragdoll.bones.length; i++) {
    const b = ragdoll.bones[i], m = ragdoll.visual[i];
    if (!m) continue;
    m.position.copy(b.position);
    m.quaternion.copy(b.quaternion);
  }
}

const _invisibleMat = new THREE.MeshBasicMaterial({ visible: false });
const _p = new THREE.Vector3();
const _n = new THREE.Vector3();
const _d = new THREE.Vector3();
const _o = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _euler = new THREE.Euler();

export { makeMoveResult };
